/**
 * ADMIN DMCA MANAGEMENT
 * 
 * Admin page for managing DMCA claims and photo attribution.
 * 
 * Features:
 * - View all claims (pending, reviewing, resolved)
 * - Take action on claims (hide, remove, add credit)
 * - Notify users
 * - View action logs
 * - Search claims
 * 
 * Route: /admin/dmca
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  Clock,
  Search,
  RefreshCw,
  X,
  EyeOff,
  Trash2,
  AtSign,
  ExternalLink,
  Mail,
  MessageSquare,
  Camera,
  User,
  Calendar,
  FileText,
  ArrowLeft,
  Ban,
  RotateCcw,
  Send
} from 'lucide-react';
import {
  getAllDMCAClaims,
  getDMCAClaimWithLogs,
  updateClaimStatus,
  resolveClaim,
  hidePhoto,
  removePhoto,
  restorePhoto,
  updatePhotoCredit,
  notifyUserAboutDMCA,
  getDMCAStats,
  searchDMCAClaims,
  findPhotoOwner
} from '../services/dmcaService';
import type {
  DMCAClaim,
  DMCAActionLog,
  ClaimStatus,
  DMCAStats
} from '../types/dmca';

export default function AdminDMCA() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Claims data
  const [claims, setClaims] = useState<DMCAClaim[]>([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [stats, setStats] = useState<DMCAStats | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<ClaimStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Selected claim for detail view
  const [selectedClaim, setSelectedClaim] = useState<DMCAClaim | null>(null);
  const [claimLogs, setClaimLogs] = useState<DMCAActionLog[]>([]);
  const [loadingClaimDetail, setLoadingClaimDetail] = useState(false);

  // Action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Notification modal
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyMessage, setNotifyMessage] = useState('');

  // Credit modal
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [creditName, setCreditName] = useState('');
  const [creditUrl, setCreditUrl] = useState('');

  // Check admin status
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUserId(user?.uid || null);

      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setIsAdmin(userDoc.data()?.isAdmin === true);
        }
      } else {
        setIsAdmin(false);
      }
      setCheckingAdmin(false);
    });
    return () => unsubscribe();
  }, []);

  // Load claims
  useEffect(() => {
    if (isAdmin) {
      loadClaims();
      loadStats();
    }
  }, [isAdmin, statusFilter]);

  const loadClaims = async () => {
    setLoadingClaims(true);
    try {
      const filter = statusFilter === 'all' ? undefined : statusFilter;
      const data = await getAllDMCAClaims(filter);
      setClaims(data);
    } catch (error) {
      console.error('Error loading claims:', error);
    } finally {
      setLoadingClaims(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await getDMCAStats();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadClaims();
      return;
    }

    setLoadingClaims(true);
    try {
      const results = await searchDMCAClaims(searchTerm);
      setClaims(results);
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoadingClaims(false);
    }
  };

  const loadClaimDetail = async (claimId: string) => {
    setLoadingClaimDetail(true);
    try {
      const result = await getDMCAClaimWithLogs(claimId);
      if (result) {
        setSelectedClaim(result.claim);
        setClaimLogs(result.logs);
      }
    } catch (error) {
      console.error('Error loading claim detail:', error);
    } finally {
      setLoadingClaimDetail(false);
    }
  };

  // Action handlers
  const handleHidePhoto = async () => {
    if (!selectedClaim || !currentUserId) return;

    setActionLoading('hide');
    try {
      const success = await hidePhoto(selectedClaim.id, selectedClaim.photoUrl, currentUserId);
      if (success) {
        await updateClaimStatus(selectedClaim.id, 'reviewing', currentUserId, 'Photo hidden pending review');
        setActionMessage({ type: 'success', text: 'Photo hidden successfully' });
        loadClaimDetail(selectedClaim.id);
        loadClaims();
      } else {
        setActionMessage({ type: 'error', text: 'Could not find photo to hide' });
      }
    } catch (error) {
      console.error('Error hiding photo:', error);
      setActionMessage({ type: 'error', text: 'Failed to hide photo' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemovePhoto = async () => {
    if (!selectedClaim || !currentUserId) return;

    if (!confirm('Are you sure you want to permanently remove this photo? This cannot be undone.')) {
      return;
    }

    setActionLoading('remove');
    try {
      const success = await removePhoto(selectedClaim.id, selectedClaim.photoUrl, currentUserId);
      if (success) {
        await resolveClaim(selectedClaim.id, 'photo_removed', currentUserId, 'Photo permanently removed');
        setActionMessage({ type: 'success', text: 'Photo removed successfully' });
        loadClaimDetail(selectedClaim.id);
        loadClaims();
        loadStats();
      } else {
        setActionMessage({ type: 'error', text: 'Could not find photo to remove' });
      }
    } catch (error) {
      console.error('Error removing photo:', error);
      setActionMessage({ type: 'error', text: 'Failed to remove photo' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRestorePhoto = async () => {
    if (!selectedClaim || !currentUserId) return;

    setActionLoading('restore');
    try {
      const success = await restorePhoto(selectedClaim.id, selectedClaim.photoUrl, currentUserId);
      if (success) {
        await resolveClaim(selectedClaim.id, 'no_action', currentUserId, 'Photo restored - claim invalid');
        setActionMessage({ type: 'success', text: 'Photo restored successfully' });
        loadClaimDetail(selectedClaim.id);
        loadClaims();
      } else {
        setActionMessage({ type: 'error', text: 'Could not find photo to restore' });
      }
    } catch (error) {
      console.error('Error restoring photo:', error);
      setActionMessage({ type: 'error', text: 'Failed to restore photo' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddCredit = async () => {
    if (!selectedClaim || !currentUserId || !creditName.trim()) return;

    setActionLoading('credit');
    try {
      const success = await updatePhotoCredit(
        selectedClaim.id,
        selectedClaim.photoUrl,
        creditName.trim(),
        creditUrl.trim() || undefined,
        currentUserId
      );
      if (success) {
        await resolveClaim(selectedClaim.id, 'credit_added', currentUserId, `Credit added: ${creditName}`);
        setActionMessage({ type: 'success', text: 'Credit added successfully' });
        setShowCreditModal(false);
        setCreditName('');
        setCreditUrl('');
        loadClaimDetail(selectedClaim.id);
        loadClaims();
        loadStats();
      } else {
        setActionMessage({ type: 'error', text: 'Could not find photo to update' });
      }
    } catch (error) {
      console.error('Error adding credit:', error);
      setActionMessage({ type: 'error', text: 'Failed to add credit' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleNotifyUser = async () => {
    if (!selectedClaim || !currentUserId || !notifyMessage.trim()) return;
    if (!selectedClaim.photoOwnerId) {
      setActionMessage({ type: 'error', text: 'Photo owner not identified yet. Hide the photo first to identify the owner.' });
      return;
    }

    setActionLoading('notify');
    try {
      await notifyUserAboutDMCA(
        selectedClaim.id,
        selectedClaim.photoOwnerId,
        notifyMessage.trim(),
        currentUserId
      );
      setActionMessage({ type: 'success', text: 'User notified successfully' });
      setShowNotifyModal(false);
      setNotifyMessage('');
      loadClaimDetail(selectedClaim.id);
    } catch (error) {
      console.error('Error notifying user:', error);
      setActionMessage({ type: 'error', text: 'Failed to notify user' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectClaim = async () => {
    if (!selectedClaim || !currentUserId) return;

    const reason = prompt('Reason for rejecting this claim (optional):');

    setActionLoading('reject');
    try {
      await updateClaimStatus(selectedClaim.id, 'invalid', currentUserId, reason || 'Claim rejected');
      setActionMessage({ type: 'success', text: 'Claim rejected' });
      loadClaimDetail(selectedClaim.id);
      loadClaims();
      loadStats();
    } catch (error) {
      console.error('Error rejecting claim:', error);
      setActionMessage({ type: 'error', text: 'Failed to reject claim' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleFindOwner = async () => {
    if (!selectedClaim) return;

    setActionLoading('findOwner');
    try {
      const owner = await findPhotoOwner(selectedClaim.photoUrl);
      if (owner) {
        setActionMessage({ type: 'success', text: `Found: ${owner.username || owner.userId} - ${owner.itemType}: ${owner.itemName}` });
        // Reload to get updated claim with owner info
        loadClaimDetail(selectedClaim.id);
      } else {
        setActionMessage({ type: 'error', text: 'Could not find this photo in any user collection' });
      }
    } catch (error) {
      console.error('Error finding owner:', error);
      setActionMessage({ type: 'error', text: 'Error searching for photo owner' });
    } finally {
      setActionLoading(null);
    }
  };

  // Format date helper
  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge helper
  const getStatusBadge = (status: ClaimStatus) => {
    const styles: Record<ClaimStatus, string> = {
      pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
      reviewing: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
      valid: 'bg-green-500/20 text-green-400 border border-green-500/30',
      invalid: 'bg-slate-500/20 text-slate-400 border border-slate-500/30',
      resolved: 'bg-purple-500/20 text-purple-400 border border-purple-500/30',
    };
    return styles[status] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
  };

  // Loading state
  if (checkingAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center glass-card p-8">
          <Shield className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-glass mb-2 font-heading">Admin Access Required</h1>
          <p className="text-glass-secondary mb-4 font-body">You don't have permission to view this page.</p>
          <Link to="/" className="text-purple-400 hover:text-purple-300 transition-default">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-4 transition-default font-body"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-glass flex items-center gap-3 font-heading">
                <Shield className="w-7 h-7 text-purple-400" />
                DMCA Claims Management
              </h1>
              <p className="text-glass-secondary mt-1 font-body">Review and manage photo attribution requests</p>
            </div>
            <button
              onClick={() => { loadClaims(); loadStats(); }}
              className="flex items-center gap-2 px-4 py-2 glass-card hover:bg-slate-700/50 transition-default text-glass font-body"
            >
              <RefreshCw className={`w-4 h-4 ${loadingClaims ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="stat-card-purple">
              <div className="text-2xl font-bold text-purple-300 font-heading">{stats.totalClaims}</div>
              <div className="text-sm text-purple-400 font-body">Total Claims</div>
            </div>
            <div className="stat-card-amber">
              <div className="text-2xl font-bold text-amber-300 font-heading">{stats.pendingClaims}</div>
              <div className="text-sm text-amber-400 font-body">Pending</div>
            </div>
            <div className="stat-card-green">
              <div className="text-2xl font-bold text-green-300 font-heading">{stats.resolvedClaims}</div>
              <div className="text-sm text-green-400 font-body">Resolved</div>
            </div>
            <div className="stat-card-red">
              <div className="text-2xl font-bold text-red-300 font-heading">{stats.photosRemoved}</div>
              <div className="text-sm text-red-400 font-body">Photos Removed</div>
            </div>
            <div className="stat-card-blue">
              <div className="text-2xl font-bold text-blue-300 font-heading">{stats.avgResolutionDays}d</div>
              <div className="text-sm text-blue-400 font-body">Avg Resolution</div>
            </div>
          </div>
        )}

        {/* Action Message */}
        {actionMessage && (
          <div className={`mb-4 p-4 rounded-xl flex items-center justify-between ${
            actionMessage.type === 'success' ? 'alert-glass-green' : 'alert-glass-red'
          }`}>
            <div className="flex items-center gap-2">
              {actionMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-400" />
              )}
              <span className={`font-body ${actionMessage.type === 'success' ? 'text-green-300' : 'text-red-300'}`}>
                {actionMessage.text}
              </span>
            </div>
            <button onClick={() => setActionMessage(null)} className="text-slate-400 hover:text-slate-300 transition-default">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Claims List */}
          <div className="lg:col-span-1">
            <div className="glass-card overflow-hidden">
              {/* Search & Filter */}
              <div className="p-4 border-b border-slate-600">
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-glass-muted" />
                    <input
                      type="text"
                      placeholder="Search claims..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="input-glass pl-10 text-sm"
                    />
                  </div>
                  <button
                    onClick={handleSearch}
                    className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-default"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex gap-2">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as ClaimStatus | 'all')}
                    className="flex-1 px-3 py-2 bg-white border border-slate-600 rounded-lg text-sm text-light-primary focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="resolved">Resolved</option>
                    <option value="invalid">Invalid</option>
                  </select>
                </div>
              </div>

              {/* Claims */}
              <div className="max-h-[600px] overflow-y-auto">
                {loadingClaims ? (
                  <div className="p-8 text-center">
                    <RefreshCw className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-2" />
                    <p className="text-glass-muted font-body">Loading claims...</p>
                  </div>
                ) : claims.length === 0 ? (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-green-400/50 mx-auto mb-2" />
                    <p className="text-glass-muted font-body">No claims found</p>
                  </div>
                ) : (
                  claims.map(claim => (
                    <button
                      key={claim.id}
                      onClick={() => loadClaimDetail(claim.id)}
                      className={`w-full p-4 border-b border-slate-700/50 text-left hover:bg-slate-700/50 transition-default ${
                        selectedClaim?.id === claim.id ? 'bg-purple-500/20' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-medium text-glass truncate flex-1 font-body">
                          {claim.claimantName}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(claim.status)}`}>
                          {claim.status}
                        </span>
                      </div>
                      <p className="text-sm text-glass-secondary truncate mb-1 font-body">
                        {claim.description.substring(0, 60)}...
                      </p>
                      <div className="flex items-center gap-2 text-xs text-glass-muted">
                        <Calendar className="w-3 h-3" />
                        {formatDate(claim.submittedAt)}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Claim Detail */}
          <div className="lg:col-span-2">
            {loadingClaimDetail ? (
              <div className="glass-card p-8 text-center">
                <RefreshCw className="w-6 h-6 text-purple-400 animate-spin mx-auto mb-2" />
                <p className="text-glass-muted font-body">Loading claim details...</p>
              </div>
            ) : selectedClaim ? (
              <div className="glass-card overflow-hidden">
                {/* Claim Header */}
                <div className="p-6 border-b border-slate-600">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-glass font-heading">{selectedClaim.claimantName}</h2>
                      <p className="text-glass-secondary flex items-center gap-2 mt-1 font-body">
                        <Mail className="w-4 h-4" />
                        {selectedClaim.claimantEmail}
                      </p>
                      {selectedClaim.claimantUrl && (
                        <a
                          href={selectedClaim.claimantUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300 flex items-center gap-1 mt-1 text-sm transition-default font-body"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {selectedClaim.claimantUrl}
                        </a>
                      )}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(selectedClaim.status)}`}>
                      {selectedClaim.status}
                    </span>
                  </div>

                  {/* Photo Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600/50">
                      <h3 className="text-sm font-medium text-glass-secondary mb-2 flex items-center gap-2 font-body">
                        <Camera className="w-4 h-4" />
                        Claimed Photo
                      </h3>
                      <a
                        href={selectedClaim.photoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-purple-400 hover:text-purple-300 break-all flex items-center gap-1 transition-default font-body"
                      >
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        View Photo
                      </a>
                      {selectedClaim.originalPhotoUrl && (
                        <a
                          href={selectedClaim.originalPhotoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:text-blue-300 break-all flex items-center gap-1 mt-2 transition-default font-body"
                        >
                          <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          Original Photo (proof)
                        </a>
                      )}
                    </div>

                    <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-600/50">
                      <h3 className="text-sm font-medium text-glass-secondary mb-2 flex items-center gap-2 font-body">
                        <User className="w-4 h-4" />
                        Photo Owner
                      </h3>
                      {selectedClaim.photoOwnerUsername ? (
                        <div>
                          <p className="text-sm text-glass font-body">{selectedClaim.photoOwnerUsername}</p>
                          <p className="text-xs text-glass-muted font-body">{selectedClaim.photoLocation}</p>
                        </div>
                      ) : (
                        <button
                          onClick={handleFindOwner}
                          disabled={actionLoading === 'findOwner'}
                          className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1 transition-default font-body"
                        >
                          {actionLoading === 'findOwner' ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <Search className="w-3 h-3" />
                          )}
                          Find Owner
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="p-6 border-b border-slate-600">
                  <h3 className="text-sm font-medium text-glass-secondary mb-2 flex items-center gap-2 font-body">
                    <FileText className="w-4 h-4" />
                    Claim Description
                  </h3>
                  <p className="text-glass whitespace-pre-wrap font-body">{selectedClaim.description}</p>
                  <div className="mt-3 flex items-center gap-4 text-sm text-glass-muted font-body">
                    <span>Requested: <strong className="text-glass-secondary">{selectedClaim.requestedAction}</strong></span>
                    <span>Submitted: <strong className="text-glass-secondary">{formatDate(selectedClaim.submittedAt)}</strong></span>
                  </div>
                </div>

                {/* Actions */}
                {selectedClaim.status !== 'resolved' && selectedClaim.status !== 'invalid' && (
                  <div className="p-6 border-b border-slate-600 bg-slate-800/30">
                    <h3 className="text-sm font-medium text-glass-secondary mb-3 font-body">Take Action</h3>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={handleHidePhoto}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'hide' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <EyeOff className="w-4 h-4" />}
                        Hide Photo
                      </button>
                      <button
                        onClick={handleRemovePhoto}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'remove' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Remove Photo
                      </button>
                      <button
                        onClick={() => {
                          setCreditName(selectedClaim.claimantName);
                          setCreditUrl(selectedClaim.claimantUrl || '');
                          setShowCreditModal(true);
                        }}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'credit' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <AtSign className="w-4 h-4" />}
                        Add Credit
                      </button>
                      <button
                        onClick={handleRestorePhoto}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'restore' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                        Restore Photo
                      </button>
                      <button
                        onClick={() => {
                          setNotifyMessage(`Your photo has been flagged for possible attribution issues. The original photographer has requested ${selectedClaim?.requestedAction === 'credit' ? 'credit be added' : selectedClaim?.requestedAction === 'remove' ? 'the photo be removed' : 'action be taken'}. Please review your collection.`);
                          setShowNotifyModal(true);
                        }}
                        disabled={!!actionLoading || !selectedClaim.photoOwnerId}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'notify' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
                        Notify User
                      </button>
                      <button
                        onClick={handleRejectClaim}
                        disabled={!!actionLoading}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-default font-body"
                      >
                        {actionLoading === 'reject' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                        Reject Claim
                      </button>
                    </div>
                  </div>
                )}

                {/* Action Logs */}
                <div className="p-6">
                  <h3 className="text-sm font-medium text-glass-secondary mb-3 flex items-center gap-2 font-body">
                    <Clock className="w-4 h-4" />
                    Action History
                  </h3>
                  {claimLogs.length === 0 ? (
                    <p className="text-glass-muted text-sm font-body">No actions yet</p>
                  ) : (
                    <div className="space-y-3">
                      {claimLogs.map(log => (
                        <div key={log.id} className="flex gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-purple-400 mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-glass font-body">{log.details}</p>
                            <p className="text-glass-muted text-xs font-body">{formatDate(log.timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="glass-card p-12 text-center">
                <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                <p className="text-glass-muted font-body">Select a claim to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notify Modal */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-glass mb-4 flex items-center gap-2 font-heading">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              Notify User
            </h3>
            <textarea
              value={notifyMessage}
              onChange={(e) => setNotifyMessage(e.target.value)}
              rows={4}
              className="textarea-glass mb-4"
              placeholder="Message to send to the user..."
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowNotifyModal(false)}
                className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700/50 transition-default font-body"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifyUser}
                disabled={!notifyMessage.trim() || actionLoading === 'notify'}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-default font-body"
              >
                {actionLoading === 'notify' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="glass-modal max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-glass mb-4 flex items-center gap-2 font-heading">
              <AtSign className="w-5 h-5 text-green-400" />
              Add Photo Credit
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">Credit Name *</label>
                <input
                  type="text"
                  value={creditName}
                  onChange={(e) => setCreditName(e.target.value)}
                  className="input-glass"
                  placeholder="Photographer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-glass-secondary mb-1 font-body">Credit URL</label>
                <input
                  type="url"
                  value={creditUrl}
                  onChange={(e) => setCreditUrl(e.target.value)}
                  className="input-glass"
                  placeholder="https://instagram.com/..."
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreditModal(false)}
                className="flex-1 px-4 py-2 border border-slate-600 text-glass-secondary rounded-lg hover:bg-slate-700/50 transition-default font-body"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCredit}
                disabled={!creditName.trim() || actionLoading === 'credit'}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 transition-default font-body"
              >
                {actionLoading === 'credit' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Add Credit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
